/**
 * The transactional EMAIL DESIGN contract — the block vocabulary a tenant
 * arranges in the email builder, the brand tokens an email renders with, and the
 * ONE renderer that turns a layout into email-safe HTML. Shared verbatim by the
 * frontend builder/preview and the operations send, so the preview and the
 * delivered message can never drift apart (each used to carry its own copy of
 * the shell).
 *
 * The renderer emits `{{ns.field}}` merge tokens in place — the caller
 * substitutes them (preview: sample values; send: the merge-field resolver
 * against an HTML-escaped context). Inline CSS only: email clients ignore
 * stylesheets.
 *
 * Runtime values live here (`.cjs`); the types that describe them live in the
 * sibling `.d.cts`. See `estimate-doc-template.cjs` for the same split.
 */

const KINDS = require("./email-kinds.cjs");

/** The brand tokens an email renders with, all resolved (never empty). */
const DEFAULT_EMAIL_BRANDING = {
  logoUrl: null,
  primaryColor: "#1F2937",
  accentColor: "#C0561F",
  bodyFont: "Inter",
  headingFont: "Inter",
};

/**
 * Email-safe font stacks. A design names a font family (the same list the
 * document builder offers); email clients only render what the recipient has
 * installed, so every family degrades to a system face of the same shape.
 */
const EMAIL_FONT_STACKS = {
  Inter: "Inter,-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif",
  Geist: "Geist,Inter,-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif",
  Arial: "Arial,Helvetica,sans-serif",
  Helvetica: "Helvetica,Arial,sans-serif",
  Roboto: "Roboto,-apple-system,'Segoe UI',Helvetica,Arial,sans-serif",
  Georgia: "Georgia,'Times New Roman',Times,serif",
};

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** True when `value` is a 6-digit hex colour (the only colour form the tokens accept). */
function isHexColor(value) {
  return typeof value === "string" && HEX_COLOR.test(value);
}

/** The email-safe stack for a font family name; unknown names get the default stack. */
function emailFontStack(name) {
  return EMAIL_FONT_STACKS[name] || EMAIL_FONT_STACKS[DEFAULT_EMAIL_BRANDING.bodyFont];
}

/**
 * Resolve the brand tokens an email renders with: the design's own override
 * wins, then the tenant/Business-Unit brand, then the defaults. Colours must be
 * hex and fonts must be a known family — anything else falls through, so a bad
 * stored value can never reach an inline style.
 */
function resolveEmailBranding(brand, override) {
  const b = brand || {};
  const o = override || {};
  const color = (key) =>
    isHexColor(o[key]) ? o[key] : isHexColor(b[key]) ? b[key] : DEFAULT_EMAIL_BRANDING[key];
  const font = (key) =>
    EMAIL_FONT_STACKS[o[key]] ? o[key] : EMAIL_FONT_STACKS[b[key]] ? b[key] : DEFAULT_EMAIL_BRANDING[key];
  const logo = typeof o.logoUrl === "string" && o.logoUrl ? o.logoUrl : typeof b.logoUrl === "string" && b.logoUrl ? b.logoUrl : null;
  // The platform mark's image: supplied by the send / preview, never by a design.
  // Server / preview supplied (never a design's), so plain http is fine for a
  // local origin; only the scheme is checked.
  const platformLogo = typeof b.platformLogoUrl === "string" && /^https?:\/\//i.test(b.platformLogoUrl.trim()) ? b.platformLogoUrl.trim() : null;
  return {
    logoUrl: logo,
    platformLogoUrl: platformLogo,
    primaryColor: color("primaryColor"),
    accentColor: color("accentColor"),
    bodyFont: font("bodyFont"),
    headingFont: font("headingFont"),
  };
}

/**
 * The one call-to-action a kind's send can resolve (from the kind registry). A
 * button pointing anywhere else is a dead link (its merge token is not in the
 * kind's supported set). Kinds without a link have no entry.
 */
const KIND_CTA = Object.fromEntries(
  Object.entries(KINDS.EMAIL_KINDS)
    .filter(([, k]) => k.cta)
    .map(([kind, k]) => [kind, { ...k.cta, token: KINDS.CTA_ACTIONS[k.cta.action] }]),
);

/**
 * A URL a design may carry (a custom button link, an image source): the logo
 * merge token, or an absolute http(s) / mailto / tel link. Anything else — a
 * `javascript:` scheme, a bare `data:` blob, an attribute-breaking value — is
 * refused, both at publish and at render.
 */
const SAFE_URL = /^(https?:\/\/[^\s"'<>]+|mailto:[^\s"'<>]+|tel:[^\s"'<>]+)$/i;
const LOGO_TOKEN = /^\{\{\s*business\.logo\s*\}\}$/;
function isSafeEmailUrl(value) {
  if (typeof value !== "string") return false;
  const v = value.trim();
  return LOGO_TOKEN.test(v) || SAFE_URL.test(v);
}

/** An image source: the logo token or an https:// URL (mailto/tel are links, not images). */
const SAFE_IMAGE_URL = /^https:\/\/[^\s"'<>]+$/i;
function isSafeImageUrl(value) {
  if (typeof value !== "string") return false;
  const v = value.trim();
  return LOGO_TOKEN.test(v) || SAFE_IMAGE_URL.test(v);
}

/** The visible text of a CTA button: the author's label, else the kind's own for that action. */
function buttonLabel(block) {
  const label = typeof block.label === "string" ? block.label.trim() : "";
  if (label) return label;
  const owner = Object.values(KINDS.EMAIL_KINDS).find((k) => k.cta && k.cta.action === block.action);
  return owner ? owner.cta.label : "Open";
}

const ATTR_ESCAPE = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
/** Escape a value for a double-quoted HTML attribute (a merge token passes through untouched). */
function escapeAttr(v) {
  return String(v == null ? "" : v).replace(/[&<>"']/g, (c) => ATTR_ESCAPE[c] || c);
}

/** The document link a CTA action points at, as a merge token (or a vetted literal URL). */
function buttonHref(block) {
  const token = KINDS.CTA_ACTIONS[block.action];
  if (token) return "{{" + token + "}}";
  return isSafeEmailUrl(block.url) ? block.url.trim() : "#";
}

/**
 * The URL-carrying fields of a layout that are NOT safe to publish — a custom
 * button URL or an image source outside the allowed forms. Empty when clean.
 */
function layoutUrlProblems(layout) {
  const out = [];
  const blocks = layout && Array.isArray(layout.blocks) ? layout.blocks : [];
  for (const b of blocks) {
    if (!b) continue;
    if (b.type === "button" && b.action === "custom" && !isSafeEmailUrl(b.url)) {
      out.push("button \"" + (b.label || "") + "\": the URL must start with https://, http://, mailto: or tel:");
    }
    if (b.type === "image" && typeof b.src === "string" && b.src.trim() && !isSafeImageUrl(b.src)) {
      out.push("image: the source must be the company logo or an https:// image URL");
    }
  }
  return out;
}

/**
 * Whether a layout carries the kind's own CTA — a button bound to the kind's
 * action. The send's only way for the customer to open the document / manage
 * the visit, so publish refuses a design without one (and the builder marks it
 * locked). A design with no `blocks` (legacy) is judged on its HTML instead.
 */
function layoutHasKindCta(layout, kind) {
  const cta = KIND_CTA[kind];
  if (!cta || !layout || !Array.isArray(layout.blocks)) return false;
  return layout.blocks.some(
    (b) =>
      b &&
      ((b.type === "button" && b.action === cta.action) ||
        // A legacy email migrated into the builder carries its CTA as a link
        // inside its preserved rich-text block.
        (typeof b.html === "string" && htmlHasKindCta(b.html, kind))),
  );
}

/**
 * The required blocks a kind's design must keep — its call-to-action button
 * and its legal block — described for the author, empty when all present.
 * Publish refuses a design that misses one; the builder marks them locked.
 */
function layoutMissingRequired(layout, kind) {
  const k = KINDS.EMAIL_KINDS[kind];
  const out = [];
  if (!k) return out;
  const blocks = layout && Array.isArray(layout.blocks) ? layout.blocks : [];
  if (k.cta && !layoutHasKindCta(layout, kind)) out.push('the "' + k.cta.label + '" button');
  if (k.legal && !blocks.some((b) => b && b.type === "legal" && b.token === k.legal)) {
    out.push("the required legal text (" + (KINDS.MERGE_FIELD_LABELS[k.legal] || k.legal).toLowerCase() + ")");
  }
  return out;
}

/** Where a header logo sits relative to its text. Anything unknown = above. */
function logoPlacement(value) {
  return value === "left" || value === "right" ? value : "above";
}

/** Whether legacy body HTML carries the kind's CTA link (`href="{{kind token}}"`). */
function htmlHasKindCta(html, kind) {
  const cta = KIND_CTA[kind];
  if (!cta || typeof html !== "string") return false;
  // A real anchor carrying the token as its href (either quote, spaces around
  // `=`) — the same shape `styleCtaButtons` turns into the button. A bare
  // `href="{{…}}"` in running text is not a link and does not count.
  return new RegExp(
    "<a\\b[^>]*href\\s*=\\s*[\"']\\s*\\{\\{\\s*" + cta.token.replace(".", "\\.") + "\\s*\\}\\}\\s*[\"']",
    "i",
  ).test(html);
}

/** A block's alignment normalised through the allowlist — never a raw stored string in a style attribute. */
const ALIGNS = ["left", "center", "right"];
function safeAlign(value) {
  return ALIGNS.includes(value) ? value : "left";
}

const TEXT_ESCAPE = { "&": "&amp;", "<": "&lt;", ">": "&gt;" };
function escapeText(v) {
  return String(v == null ? "" : v).replace(/[&<>]/g, (c) => TEXT_ESCAPE[c] || c);
}

const SPACER_PX = { sm: 8, md: 20, lg: 40 };
const PAD = "padding-left:28px;padding-right:28px;";
const INK = "#111827";
const MUTED = "#6b7280";
const HAIRLINE = "#f0f0f0";

/** The inline style of the CTA button for a brand (background = primary colour). */
function ctaStyle(branding) {
  return (
    "display:inline-block;background:" + branding.primaryColor + ";color:#ffffff;text-decoration:none;" +
    "padding:12px 22px;border-radius:8px;font-weight:600;font-size:14px;font-family:" + emailFontStack(branding.bodyFont) + ";"
  );
}

/**
 * The card shell every email sits in: neutral page, white card, and a brand
 * rule along the top in the design's PRIMARY colour — the one place the brand
 * colour always shows, even on a design with no header block. Fonts come from
 * the brand, so the body reads in the design's body face.
 */
function shellOpen(branding) {
  return (
    '<div style="margin:0;padding:24px;background:#f3f4f6;font-family:' + emailFontStack(branding.bodyFont) + ';">' +
    '<div style="max-width:600px;margin:0 auto;background:#ffffff;border-radius:12px;overflow:hidden;border:1px solid #e5e7eb;border-top:4px solid ' + branding.primaryColor + ';">'
  );
}
/**
 * The platform mark under the card, on the canvas — the same "Powered by
 * Notifi · © year" the transactional templates always carried. Part of the
 * shell, not a block: a design cannot drop it.
 */
function shellClose(branding) {
  const mark = branding && branding.platformLogoUrl
    ? '<div style="font-size:11px;letter-spacing:0.04em;text-transform:uppercase;color:#9ca3af;margin-bottom:6px;">Powered by</div>' +
      '<img src="' + escapeAttr(branding.platformLogoUrl) + '" alt="Notifi" width="96" style="width:96px;height:auto;display:inline-block;opacity:0.55;" />'
    : '<div style="font-size:13px;font-weight:700;letter-spacing:0.02em;color:#6b7280;">Powered by Notifi</div>';
  return (
    "</div>" +
    '<div style="max-width:600px;margin:0 auto;padding:20px 8px 4px;text-align:center;color:#9ca3af;font-size:12px;line-height:1.6;">' +
    mark +
    "<div>&copy; " + new Date().getFullYear() + " Notifi, a division of SwiftBel, Inc. All rights reserved.</div>" +
    "</div></div>"
  );
}

function renderBlock(block, branding) {
  switch (block.type) {
    case "header": {
      const textStyle = "font-size:20px;font-weight:700;color:" + branding.primaryColor + ";line-height:1.3;font-family:" + emailFontStack(branding.headingFont) + ";";
      const wrap = PAD + "padding-top:24px;padding-bottom:20px;border-bottom:1px solid " + HAIRLINE + ";";
      const placement = block.showLogo ? logoPlacement(block.logoPlacement) : "above";
      if (block.showLogo && placement !== "above") {
        // Logo beside the text (a presentation table: the one layout every
        // mail client honours), vertically centred; `align` picks the side
        // the pair sits on.
        const logoCell = '<td style="vertical-align:middle;padding-' + (placement === "left" ? "right" : "left") + ':16px;white-space:nowrap;">' +
          '<img src="{{business.logo}}" alt="" style="max-height:48px;height:auto;width:auto;display:block;" /></td>';
        const textCell = '<td style="vertical-align:middle;' + textStyle + '">' + (block.html || "") + "</td>";
        const cells = placement === "left" ? logoCell + textCell : textCell + logoCell;
        const tableAlign = safeAlign(block.align);
        return (
          '<div style="' + wrap + "text-align:" + tableAlign + ';">' +
          '<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;display:inline-table;' + (tableAlign === "left" ? "" : "margin:0 auto;") + (tableAlign === "right" ? "margin-right:0;" : "") + '"><tr>' +
          cells + "</tr></table></div>"
        );
      }
      const logo = block.showLogo
        ? '<img src="{{business.logo}}" alt="" style="max-height:48px;height:auto;width:auto;display:inline-block;margin-bottom:8px;" />'
        : "";
      return (
        '<div style="' + wrap + "text-align:" + safeAlign(block.align) + ";" + textStyle + '">' +
        logo + (block.html || "") + "</div>"
      );
    }
    case "text":
      return '<div style="' + PAD + "padding-top:12px;padding-bottom:12px;color:" + INK + ';font-size:15px;line-height:1.6;">' + (block.html || "") + "</div>";
    case "button":
      return (
        '<div style="' + PAD + "padding-top:12px;padding-bottom:12px;text-align:" + safeAlign(block.align) + ';">' +
        '<a href="' + escapeAttr(buttonHref(block)) + '" style="' + ctaStyle(branding) + '">' + escapeText(buttonLabel(block)) + "</a></div>"
      );
    case "image": {
      // Only a vetted source renders; anything else shows the placeholder.
      const src = isSafeImageUrl(block.src) ? block.src.trim() : "";
      const inner = src
        ? '<img src="' + escapeAttr(src) + '" alt="" style="max-width:100%;max-height:200px;height:auto;display:inline-block;" />'
        : '<div style="display:inline-block;padding:24px 40px;border:1px dashed #d1d5db;border-radius:8px;color:#9ca3af;font-size:13px;">Image</div>';
      return '<div style="' + PAD + "padding-top:12px;padding-bottom:12px;text-align:" + safeAlign(block.align) + ';">' + inner + "</div>";
    }
    case "divider":
      return '<div style="' + PAD + '"><div style="border-top:1px solid #eee;margin:8px 0;"></div></div>';
    case "spacer": {
      const px = SPACER_PX[block.size] || SPACER_PX.md;
      return '<div style="height:' + px + "px;line-height:" + px + 'px;">&nbsp;</div>';
    }
    case "footer": {
      const wrap = PAD + "padding-top:20px;padding-bottom:24px;border-top:1px solid " + HAIRLINE + ";color:" + MUTED + ";font-size:12px;line-height:1.6;";
      if (block.columns === 2) {
        // Two columns (a presentation table): left column = the text, right
        // column = the second text, right-aligned. Stacks in narrow clients.
        return (
          '<div style="' + wrap + '">' +
          '<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;"><tr>' +
          '<td style="vertical-align:top;width:50%;padding-right:12px;">' + (block.html || "") + "</td>" +
          '<td style="vertical-align:top;width:50%;padding-left:12px;text-align:right;">' + (block.htmlRight || "") + "</td>" +
          "</tr></table></div>"
        );
      }
      return '<div style="' + wrap + '">' + (block.html || "") + "</div>";
    }
    case "legal": {
      // Required, non-editable text the send supplies (a financing disclosure,
      // a CAN-SPAM footer). Only a known `legal.*` token renders; it prints
      // pre-line so multi-line notices keep their lines, and nothing when the
      // send has nothing to disclose.
      const token = typeof block.token === "string" && /^legal\.[a-zA-Z]+$/.test(block.token) ? block.token : null;
      if (!token) return "";
      return (
        '<div style="' + PAD + "padding-top:12px;padding-bottom:12px;color:" + MUTED + ';font-size:11px;line-height:1.5;white-space:pre-line;">{{' + token + "}}</div>"
      );
    }
    default:
      return "";
  }
}

/** Render a block layout to token-HTML inside the branded card shell. */
function renderEmailLayout(layout, branding) {
  const b = resolveEmailBranding(branding);
  const blocks = layout && Array.isArray(layout.blocks) ? layout.blocks : [];
  return shellOpen(b) + blocks.map((blk) => renderBlock(blk, b)).join("") + shellClose(b);
}

/**
 * Wrap a LEGACY plain body (authored before the block builder) in the same
 * branded shell, with a business-name header and a contact footer — so an old
 * email still goes out looking like a design.
 */
function renderEmailShell(bodyHtml, branding) {
  const b = resolveEmailBranding(branding);
  return (
    shellOpen(b) +
    '<div style="' + PAD + "padding-top:24px;padding-bottom:20px;border-bottom:1px solid " + HAIRLINE + ";font-size:20px;font-weight:700;color:" + b.primaryColor + ";font-family:" + emailFontStack(b.headingFont) + ';">{{business.displayName}}</div>' +
    '<div style="padding:28px;color:' + INK + ';font-size:15px;line-height:1.6;">' + bodyHtml + "</div>" +
    '<div style="' + PAD + "padding-top:20px;padding-bottom:24px;border-top:1px solid " + HAIRLINE + ";color:" + MUTED + ';font-size:12px;line-height:1.6;">' +
    "<div>{{business.displayName}}</div><div>{{business.address}}</div><div>{{business.phone}} · {{business.email}}</div></div>" +
    shellClose(b)
  );
}

/**
 * Style a legacy body's CTA link (`href="{{kind token}}"`) into the brand
 * button — the rich-text editor cannot author a styled button. Ordinary links
 * stay links; a link that already carries a style is left alone.
 */
function styleCtaButtons(html, branding) {
  const b = resolveEmailBranding(branding);
  // The same href forms `htmlHasKindCta` accepts (either quote, spaces around
  // `=`), so every publishable legacy CTA renders as the button.
  return html.replace(
    /<a\b([^>]*href\s*=\s*["']\s*\{\{\s*(?:estimate\.viewUrl|invoice\.payUrl|appointment\.manageUrl)\s*\}\}\s*["'][^>]*)>/gi,
    (match, attrs) => (/\sstyle=/i.test(attrs) ? match : "<a " + attrs.trim() + ' style="' + ctaStyle(b) + '">'),
  );
}

module.exports = {
  DEFAULT_EMAIL_BRANDING,
  EMAIL_FONT_STACKS,
  KIND_CTA,
  isHexColor,
  emailFontStack,
  resolveEmailBranding,
  buttonHref,
  isSafeEmailUrl,
  isSafeImageUrl,
  escapeAttr,
  safeAlign,
  layoutUrlProblems,
  layoutMissingRequired,
  layoutHasKindCta,
  htmlHasKindCta,
  renderEmailLayout,
  renderEmailShell,
  styleCtaButtons,
};
